import React, { createContext, useContext, useEffect, useState } from 'react';
import { api } from './api';

interface AgentAliasContextType {
  aliasMap: Record<string, string>;
  resolveAgent: (agentId: string, recording?: any) => string;
  refreshMap: () => void;
}

const AgentAliasContext = createContext<AgentAliasContextType>({
  aliasMap: {},
  resolveAgent: (id) => id,
  refreshMap: () => {},
});

function formatAgentFallback(agentId: string): string {
  if (!agentId || agentId === 'unknown') return '-';
  return agentId.split('-').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
}

export function AgentAliasProvider({ children }: { children: React.ReactNode }) {
  const [aliasMap, setAliasMap] = useState<Record<string, string>>({});

  const loadMap = () => {
    api.getAgentAliasMap()
      .then((data: any) => setAliasMap(data.map || {}))
      .catch(() => {}); // silent fail — fallback to title-case
  };

  useEffect(() => { loadMap(); }, []);

  const resolveAgent = (agentId: string, recording?: any): string => {
    if (!agentId || agentId === 'unknown') return '-';
    // Priority: stamped name on record > alias map > title-case fallback
    if (recording?.resolvedAgentName) return recording.resolvedAgentName;
    return aliasMap[agentId] || formatAgentFallback(agentId);
  };

  return (
    <AgentAliasContext.Provider value={{ aliasMap, resolveAgent, refreshMap: loadMap }}>
      {children}
    </AgentAliasContext.Provider>
  );
}

export function useAgentAlias() {
  return useContext(AgentAliasContext);
}
